import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { User, Mail, Phone, Calendar, Shield, LogOut } from 'lucide-react';
import LoadingSpinner from './components/LoadingSpinner';

export default function AdminProfilePage() {
  const { isArabic } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    loadAdminProfile();
  }, []);

  async function loadAdminProfile() {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/signin?admin=true');
        return;
      }

      // Get admin profile
      const { data: adminProfile, error: profileError } = await supabase
        .from('admins')
        .select('*')
        .eq('auth_user_id', user.id)
        .single();

      if (profileError) throw profileError;

      setAdminData(adminProfile);
      setFormData({
        full_name: adminProfile.full_name || '',
        phone: adminProfile.phone || '',
        email: adminProfile.email || ''
      });
    } catch (err) {
      console.error('Error loading admin profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateProfile(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();

      // Update admin profile
      const { error: updateError } = await supabase
        .from('admins')
        .update({
          full_name: formData.full_name,
          phone: formData.phone
        })
        .eq('auth_user_id', user.id);

      if (updateError) throw updateError;

      setSuccess(isArabic ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully');

      // Reload profile
      await loadAdminProfile();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    try {
      await supabase.auth.signOut();
      localStorage.clear();
      navigate('/signin');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  }

  function handleInputChange(e) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingSpinner size={80} loadingMessage={isArabic ? 'جاري تحميل الملف الشخصي...' : 'Loading profile...'} isArabic={isArabic} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'الملف الشخصي' : 'Admin Profile'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'إدارة معلومات حسابك الإداري'
            : 'Manage your admin account information'}
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <Shield size={20} color="#c00" />
          <p style={{ color: '#c00', margin: 0 }}>{error}</p>
        </div>
      )}

      {success && (
        <div style={{
          background: '#efe',
          border: '1px solid #cfc',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <Shield size={20} color="#0a0" />
          <p style={{ color: '#0a0', margin: 0 }}>{success}</p>
        </div>
      )}

      {/* Profile Card */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: 32,
        marginBottom: 24
      }}>
        {/* Profile Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          marginBottom: 32,
          paddingBottom: 24,
          borderBottom: '2px solid #f0f0f0'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7426 0%, #FF9800 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 32,
            fontWeight: 700
          }}>
            {formData.full_name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: '#232B3E', margin: '0 0 8px 0' }}>
              {formData.full_name}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666', fontSize: 14 }}>
              <Shield size={16} color="#FF7426" />
              <span style={{ fontWeight: 600, color: '#FF7426' }}>
                {isArabic ? 'مسؤول النظام' : 'System Administrator'}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleUpdateProfile}>
          <div style={{ display: 'grid', gap: 20 }}>
            {/* Full Name */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: '#232B3E',
                marginBottom: 8
              }}>
                <User size={16} />
                {isArabic ? 'الاسم الكامل' : 'Full Name'}
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Email (Read-only) */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: '#232B3E',
                marginBottom: 8
              }}>
                <Mail size={16} />
                {isArabic ? 'البريد الإلكتروني' : 'Email'}
              </label>
              <input
                type="email"
                value={formData.email}
                disabled
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 14,
                  background: '#f5f5f5',
                  color: '#999',
                  cursor: 'not-allowed'
                }}
              />
              <p style={{ fontSize: 12, color: '#999', margin: '8px 0 0 0' }}>
                {isArabic
                  ? 'البريد الإلكتروني لا يمكن تغييره'
                  : 'Email address cannot be changed'}
              </p>
            </div>

            {/* Phone */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: '#232B3E',
                marginBottom: 8
              }}>
                <Phone size={16} />
                {isArabic ? 'رقم الهاتف' : 'Phone Number'}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={isArabic ? '+973 XXXX XXXX' : '+973 XXXX XXXX'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Account Created */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: '#232B3E',
                marginBottom: 8
              }}>
                <Calendar size={16} />
                {isArabic ? 'تاريخ الإنشاء' : 'Account Created'}
              </label>
              <div style={{
                padding: '12px 16px',
                background: '#f7f9fb',
                borderRadius: 8,
                fontSize: 14,
                color: '#666'
              }}>
                {new Date(adminData?.created_at).toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {/* Status */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: '#232B3E',
                marginBottom: 8
              }}>
                <Shield size={16} />
                {isArabic ? 'الحالة' : 'Status'}
              </label>
              <div style={{
                padding: '12px 16px',
                background: adminData?.is_active ? '#e8f5e9' : '#ffebee',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                color: adminData?.is_active ? '#4CAF50' : '#f44336'
              }}>
                {adminData?.is_active
                  ? (isArabic ? '✓ نشط' : '✓ Active')
                  : (isArabic ? '✗ غير نشط' : '✗ Inactive')}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            marginTop: 32,
            display: 'flex',
            gap: 12,
            paddingTop: 24,
            borderTop: '2px solid #f0f0f0'
          }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: '#FF7426',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => !saving && (e.target.style.background = '#ff8c4d')}
              onMouseLeave={(e) => !saving && (e.target.style.background = '#FF7426')}
            >
              {saving
                ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                : (isArabic ? 'حفظ التغييرات' : 'Save Changes')}
            </button>

            <button
              type="button"
              onClick={handleSignOut}
              style={{
                padding: '12px 24px',
                background: '#f44336',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#d32f2f'}
              onMouseLeave={(e) => e.target.style.background = '#f44336'}
            >
              <LogOut size={18} />
              {isArabic ? 'تسجيل الخروج' : 'Sign Out'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
